import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';

import { PhoneIcon, LockIcon, CheckmarkIcon } from '@icons';
import { getBrandBaseCurrency } from '@utils/get-brand-base-currency';
import { getBrandBaseLanguage } from '@utils/get-brand-base-language';
import { getBrandPhoneLength } from '@utils/get-brand-phone-length';
import { getBrandPhoneMask } from '@utils/get-brand-phone-mask';

import { useModals } from '@shared/contexts';

import { useMutationRegister, RegisterData } from '@hooks/api/auth/use-mutation-register';

import { Button } from '@components/button';
import { Input } from '@components/input';
import { ModalName } from '@components/modals/types.ts';
import { Separator } from '@components/separator';

import { config } from '@/config.ts';

import * as Styled from './register-form.styled';

type OwnProps = {
	shouldReload?: boolean;
	closeModal: () => void;
};

const RegisterForm: React.FC<OwnProps> = ({ shouldReload, closeModal }) => {
	const { t } = useTranslation(['errors', 'reg']);
	const { openModal } = useModals();

	const phoneMask = getBrandPhoneMask();
	const phoneLength = getBrandPhoneLength();
	const brandCurrency = getBrandBaseCurrency();
	const brandLanguage = getBrandBaseLanguage();

	const brandName = config.BRAND;
	const isCom2 = brandName === 'com2';

	const regKey = isCom2 ? 'reg:more_than_21_com2' : 'reg:more_than_21';

	const { mutate: register, isPending } = useMutationRegister(shouldReload);

	const [inputValues, setInputValues] = React.useState({
		phone: '',
		password: ''
	});

	const [isOfferChecked, setIsOfferChecked] = React.useState<boolean>(false);
	const [isBonusChecked, setIsBonusChecked] = React.useState<boolean>(false);

	const ref = React.useRef(null);
	const inputRef = React.useRef(null);

	const [phoneError, setPhoneError] = React.useState<boolean>(false);
	const [passwordErrors, setPasswordErrors] = React.useState({
		minLength: false,
		hasNumber: false
	});
	const [error, setError] = React.useState<string | null>(null);

	const handlePhoneChange = (value: string) => {
		setInputValues(prev => ({
			...prev,
			phone: value
		}));
		if (value.replace(/\D/g, '').length !== phoneLength) {
			setPhoneError(true);
		} else {
			setPhoneError(false);
		}
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setInputValues(prev => ({ ...prev, password: value }));

		const hasNum = /\d/.test(value);
		const isMinLength = /^.{6,}$/.test(value);

		setPasswordErrors({
			minLength: !isMinLength,
			hasNumber: !hasNum
		});
	};

	const isFormValid =
		inputValues.phone.replace(/\D/g, '').length === phoneLength &&
		!phoneError &&
		!passwordErrors.minLength &&
		!passwordErrors.hasNumber &&
		isOfferChecked;

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!isFormValid) return;

		const registerData: RegisterData = {
			phone: inputValues.phone,
			password: inputValues.password,
			isOfferChecked,
			currency: brandCurrency,
			language: brandLanguage,
			isBonusChecked
		};

		register(registerData, {
			onError: (error: any) => {
				if (error.response?.data?.fieldErrors?.phone) {
					if (error.response.data.fieldErrors.phone[0].type === 'AllowedChars') {
						setError(t('errors:not_supported'));
						setPhoneError(true);
					}
				} else if (error.response?.data?.modelErrors) {
					if (error.response.data.modelErrors[0].type === 'PhoneCurrencyUnique') {
						setError(t('errors:number_registered'));
						setPhoneError(true);
					}
				} else {
					setError(t('errors:error_data'));
				}
			},
			onSuccess: () => {
				closeModal();
			}
		});
	};

	const handleOfferClick = () => {
		openModal(ModalName.OFFER);
	};

	const handleBonusClick = () => {
		openModal(ModalName.BONUS);
	};

	return (
		<Styled.FormWrapper onSubmit={handleSubmit}>
			<Styled.MaskedInputWrapper>
				<Styled.Label>
					{t('phone')} <span>*</span>
				</Styled.Label>

				<Styled.MaskedInput $hasError={phoneError}>
					<Styled.InputIcon>
						<PhoneIcon />
					</Styled.InputIcon>
					<IMaskInput
						mask={phoneMask}
						required
						lazy={false}
						unmask={true}
						ref={ref}
						inputRef={inputRef}
						placeholderChar='x'
						value={inputValues.phone}
						onAccept={handlePhoneChange}
					/>
				</Styled.MaskedInput>
			</Styled.MaskedInputWrapper>

			<Separator height={16} />

			<Input
				label={t('password')}
				type='password'
				isPassword
				isRequired
				placeholder={t('password')}
				icon={LockIcon}
				required
				value={inputValues.password}
				name='password'
				onChange={handlePasswordChange}
			/>

			<Styled.PasswordConditions>
				<Styled.PasswordCondition $hasError={passwordErrors.minLength}>
					{t('errors:min_6_symbols')}
				</Styled.PasswordCondition>
				<Styled.PasswordCondition $hasError={passwordErrors.hasNumber}>
					{t('errors:min_1_number')}
				</Styled.PasswordCondition>
			</Styled.PasswordConditions>

			<Separator height={16} />

			<Styled.CheckboxOption>
				<Styled.CheckboxText>
					{t(regKey)}{' '}
					{!isCom2 && <Styled.CheckboxLink onClick={handleOfferClick}>{t('reg:accept_offer')}</Styled.CheckboxLink>}
				</Styled.CheckboxText>
				<Styled.CheckboxIcon $isChecked={isOfferChecked} onClick={() => setIsOfferChecked(!isOfferChecked)}>
					{isOfferChecked && <CheckmarkIcon />}
				</Styled.CheckboxIcon>
			</Styled.CheckboxOption>

			<Separator height={12} />

			<Styled.CheckboxOption>
				<Styled.CheckboxText>
					{t('reg:activate_bonus')}{' '}
					{!isCom2 && <Styled.CheckboxLink onClick={handleBonusClick}>{t('reg:bonus_offer')}</Styled.CheckboxLink>}
				</Styled.CheckboxText>
				<Styled.CheckboxIcon $isChecked={isBonusChecked} onClick={() => setIsBonusChecked(!isBonusChecked)}>
					{isBonusChecked && <CheckmarkIcon />}
				</Styled.CheckboxIcon>
			</Styled.CheckboxOption>

			{error && <Styled.Error>{error}</Styled.Error>}

			<Separator height={24} />

			<Button fluid color='primary' disabled={isPending || !isFormValid}>
				{t('reg:register')}
			</Button>

			<Separator height={16} />
		</Styled.FormWrapper>
	);
};

export { RegisterForm };
